import {
  Article,
  ArticleAction,
  ArticleImage,
  ArticleList,
  ArticleSubtitle,
  ArticleTitle,
} from "@babackapp/customer-ui/components/article";
import { Button } from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
} from "@babackapp/customer-ui/components/header";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { useAddLineItem } from "@babackapp/return-widget-components/hooks/use-add-line-item";
import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { useReturnableItems } from "@babackapp/return-widget-components/hooks/use-returnable-items";
import type { OrderLineItem } from "@babackapp/return-widget-components/types";
import { ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useWidgetParams } from "../../hooks/use-widget-params";

export function LineItemSelection() {
  const { orderId, shopSlug, shopId } = useWidgetParams();
  const allReturnableItems = useReturnableItems(orderId, shopSlug);
  const draft = useDraftReturn(orderId);
  const draftIds = new Set(draft?.lineItems.map((li) => li.lineItemId) ?? []);
  const returnableLineItems = allReturnableItems.filter(
    (li) => !draftIds.has(li.line_item_id),
  );

  return (
    <WidgetPage>
      <Header>
        <HeaderTitle>Sélectionnez un article</HeaderTitle>
        <HeaderDescription>
          Sélectionnez l'article que vous souhaitez retourner.
        </HeaderDescription>
      </Header>

      <ArticleList className="cursor-pointer max-h-96 overflow-y-auto">
        {returnableLineItems.map((lineItem) => (
          <LineItemRow
            key={lineItem.line_item_id}
            lineItem={lineItem}
            orderId={orderId}
            shopId={shopId}
          />
        ))}
      </ArticleList>
    </WidgetPage>
  );
}

type LineItemRowProps = {
  lineItem: OrderLineItem;
  orderId: string;
  shopId: string;
};

function LineItemRow({ lineItem, orderId, shopId }: LineItemRowProps) {
  const navigate = useNavigate();
  const addLineItem = useAddLineItem(orderId, shopId);

  const handleSelect = () => {
    addLineItem({
      lineItemId: lineItem.line_item_id,
      quantity: 1,
      reasonId: null,
      comment: null,
      imageUrls: [],
      resolution: null,
      resolutionOption: null,
      exchangeVariantId: null,
    });
    navigate(`${lineItem.line_item_id}/reason`);
  };

  const options = [
    lineItem.variant?.option1,
    lineItem.variant?.option2,
    lineItem.variant?.option3,
  ]
    .filter(Boolean)
    .filter((o) => o !== "Default Title")
    .join(" • ");

  return (
    <Article onClick={handleSelect} className="cursor-pointer">
      <ArticleImage
        src={lineItem.image}
        layoutId={`img-${lineItem.line_item_id}`}
      />
      <ArticleTitle>{lineItem.title}</ArticleTitle>
      {options && <ArticleSubtitle>{options}</ArticleSubtitle>}
      <ArticleAction>
        <Button
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleSelect();
          }}
        >
          Retourner
          <ChevronRight />
        </Button>
      </ArticleAction>
    </Article>
  );
}
